/**
 * Use promotional rails to surface and feature more than one promotional card at a time.
 *
 * @module views/components/Rail
 * @memberof -Common
 */
import './Rail.scss';
import 'swiper/swiper-bundle.min.css';
import 'swiper/swiper.min.css';

import React from 'react';

import PropTypes from 'prop-types';
import SwiperCore, {
  A11y,
  Keyboard, Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import palette_variables from 'web-styles/src/_exports.module.scss';

import Button from '@ulta/core/components/Button/Button';
import Text from '@ulta/core/components/Text/Text';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import PromotionalCard from '@ulta/modules/PromotionalCard/PromotionalCard';

import { handleInactiveSwiperSlides } from '@ulta/utils/carouselUtils/carouselUtils';
import { constants } from '@ulta/utils/constants/constants';

import RailTitle from '../RailTitle/RailTitle';

// install Swiper modules
SwiperCore.use( [Navigation, Keyboard, A11y] );

let swiperCounter = 1;

/**
 * Represents a Rail component
 *
 * @method
 * @param {RailProps} props - React properties passed from composition
 * @returns Rail
 */
export const Rail = function( props ){
  const {
    title,
    items,
    sliderPerViewXL,
    sliderPerViewLG,
    sliderPerViewMD,
    slidesPerGroupXL,
    slidesPerGroupLG,
    slidesPerGroupMD,
    slidesPerGroupSM,
    _railADAmeta,
    _railMeta,
    alignment,
    previousClickAction,
    nextClickAction,
    viewAllAction
  } = props;
  const { carouselAccessibility } = _railADAmeta || {};
  const { nextAccessibility, previousAccessibility } = _railMeta || {};

  swiperCounter += 1;
  const isSingleItem = items?.length === 1;

  if( !items ){
    return null;
  }

  return (
    <div className='Rail'>
      { title &&
        <div className='Rail__header'>
          <RailTitle
            title={ title }
            navLink={ viewAllAction }
          />
        </div>
      }
      { items?.length &&
        <div className='Rail__itemCount'>
          <Text
            textStyle='body-3'
            htmlTag='p'
            color='neutral-600'
          >
            { items?.length } items
          </Text>
        </div>
      }
      <div className='Rail__carousel'
        role='region'
      >
        { carouselAccessibility &&
        <p className='sr-only'>{ carouselAccessibility }</p>
        }
        <div className={ `Rail__carousel__Pagination Rail__carousel__Pagination-${swiperCounter}` }>
          <Button
            variant='navigation'
            iconImage='CaretBack'
            iconSize='lg'
            className='Rail__carousel__Pagination__button Rail__carousel__Pagination__button--back'
            ariaLabel={ previousAccessibility }
            ariaHiddenIcon={ true }
            action={ previousClickAction }
          />
          <Button
            variant='navigation'
            iconImage='CaretForward'
            iconSize='lg'
            className={ 'Rail__carousel__Pagination__button Rail__carousel__Pagination__button--forward' }
            ariaLabel={ nextAccessibility }
            ariaHiddenIcon={ true }
            action={ nextClickAction }
          />
        </div>
        <Swiper
          onSwiper={ ( swiper ) => handleInactiveSwiperSlides( swiper ) }
          onSlideChange={ ( swiper ) => handleInactiveSwiperSlides( swiper ) }
          freeMode={ true }
          watchOverflow={ true }
          watchSlidesProgress={ true }
          watchSlidesVisibility={ true }
          speed={ 600 }
          preloadImages={ true }
          spaceBetween={ 16 }
          navigation={ {
            prevEl: `.Rail__carousel__Pagination-${swiperCounter} .Rail__carousel__Pagination__button--back`,
            nextEl: `.Rail__carousel__Pagination-${swiperCounter} .Rail__carousel__Pagination__button--forward`,
            disabledClass: `Rail__carousel__Pagination__button--disabled`
          } }
          breakpoints={ {
            [palette_variables.breakpointXL]: {
              slidesPerView: sliderPerViewXL ? sliderPerViewXL : 4,
              slidesPerGroup: slidesPerGroupXL ? slidesPerGroupXL : 1
            },
            [palette_variables.breakpointLG]: {
              slidesPerView: sliderPerViewLG ? sliderPerViewLG : 3,
              slidesPerGroup: slidesPerGroupLG ? slidesPerGroupLG : 1
            },
            [palette_variables.breakpointMD]: {
              slidesPerView: sliderPerViewMD ? sliderPerViewMD : 3,
              slidesPerGroup: slidesPerGroupMD ? slidesPerGroupMD : 1
            },
            [palette_variables.breakpointSM]: {
              slidesPerView: isSingleItem ? 1 : 1.6,
              slidesPerGroup: slidesPerGroupSM ? slidesPerGroupSM : 1
            }
          } }
        >
          { items?.length > 0 && items?.map( ( product, productIndex )=>(
            <SwiperSlide key={ `Rail${ productIndex + 1 } ` }>
              <PromotionalCard
                { ...spreadProps( { ...product } ) }
                alignment={ alignment }
              />
            </SwiperSlide>
          ) )
          }
        </Swiper>
      </div>
    </div>
  );
};

/**
 * Property type definitions
 * @typedef RailProps
 * @type {object}
 * @property {string} title - Sets the headline title.
 * @property {string} subtitle - Sets the subtitle.
 * @property {object} viewAllAction - sets the url for action label.
 * @property {array} items - Set the Item array.
 * @property {string} backgroundColor - Sets the backgroundColor
 * @property {string} alignment - sets the alignment for PromotionalCard component.
 * @property {boolean} isGrid - sets the value true/false based on value
 * @property {object} previousClickAction - The prop that holds the action for the previous button
 * @property {object} nextClickAction - The prop that holds the action for the next button.
 * @property {object} _railADAmeta - sets carouselAccessibility
 * @property {object} _railMeta - sets nextAccessibility, previousAccessibility
 */
export const propTypes =  {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  viewAllAction: PropTypes.object,
  items: PropTypes.array,
  backgroundColor: PropTypes.string,
  alignment: PropTypes.string,
  isGrid: PropTypes.bool,
  _railADAmeta: PropTypes.object,
  _railMeta: PropTypes.object,
  previousClickAction: PropTypes.object,
  nextClickAction: PropTypes.object
};

/**
 * Default values for passed properties
 *
 * @type {RailProps}
 * @property {string} railAccessibility='carousel' - The default initial values for the railAccessibility.
 * @property {object} INTERSECTION_OBSERVER_OPTIONS - The default initial values for intersection observer.
 */
export const defaultProps =  {
  railAccessibility: 'carousel',
  ...constants.INTERSECTION_OBSERVER_OPTIONS
};

Rail.propTypes = propTypes;
Rail.defaultProps = defaultProps;

export default Rail;
